import { useState } from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Box, Text, Center, HStack, Spacer } from "@chakra-ui/react"
import { useTranslations } from "next-intl"
import { Button } from "@/components/ui/button"
import KeywordNotificationSettingsSheet from "./KeywordNotificationSettingsSheet"

interface NotificationChannel {
  id: string
  name: string
  type: string
  isActive: boolean
}

interface NotificationConfiguration {
  id: string
  interval: number
  timeConstraints?: {
    schedule: {
      weekday: {
        times: string[]
      }
    }
    timezone: string
  }
}

interface KeywordNotification {
  hasSubscription: boolean
  isActive: boolean
  subscriptionId: string | null
  channels: NotificationChannel[]
  configuration: NotificationConfiguration | null
}

interface Keyword {
  id: string
  keyword: string
  createdAt?: string
  threadsUserId?: string
  notification?: KeywordNotification
}

interface KeywordData {
  data: Keyword[]
}

function NotificationKeywordList({ data }: { data: KeywordData }) {
  const t = useTranslations("KeywordNotificationDashboard.KeywordList")
  const keywords = data.data
  const [selectedKeywordId, setSelectedKeywordId] = useState<string | null>(null)
  const [isSheetOpen, setIsSheetOpen] = useState(false)

  const handleOpenSettings = (keywordId: string) => {
    setSelectedKeywordId(keywordId)
    setIsSheetOpen(true)
  }

  const handleCloseSheet = () => {
    setIsSheetOpen(false)
    setSelectedKeywordId(null)
  }

  const tableComponent = (
    <div className="w-full">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("table.header")}</TableHead>
            <TableHead className="text-right"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {keywords.map((keyword: Keyword) => (
            <TableRow key={keyword.id}>
              <TableCell>
                <div className="flex items-center gap-2">
                  <span
                    className={`w-2 h-2 rounded-full ${
                      keyword.notification?.isActive
                        ? "bg-green-500"
                        : "bg-gray-400"
                    }`}
                  />
                  {keyword.keyword}
                </div>
              </TableCell>
              <TableCell className="text-right">
                <HStack>
                  <Spacer />
                  <Button onClick={() => handleOpenSettings(keyword.id)}>
                    {t("table.newPostNotification")}
                  </Button>
                </HStack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <KeywordNotificationSettingsSheet
        keywordId={selectedKeywordId}
        isOpen={isSheetOpen}
        onClose={handleCloseSheet}
      />
    </div>
  )

  const emptyView = (
    <Box height="340px">
      <Center height="100%">
        <Text as="b" fontSize="xl">
          {t("emptyState")}
        </Text>
      </Center>
    </Box>
  )

  return keywords.length == 0 ? emptyView : tableComponent
}

export default NotificationKeywordList
